import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { userAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Building2, Save, MapPin, FileCheck, Banknote, Shield, AlertCircle, CheckCircle, ImagePlus, Trash2 } from 'lucide-react';

const initialState = {
  raison_sociale: '',
  adresse: '',
  code_postal: '',
  ville: '',
  siren: '',
  siret: '',
  code_ape: '',
  regime_tva: 'franchise',
  tva_intracom: '',
  rc_pro_assureur: '',
  rc_pro_contrat: '',
  iban: '',
  bic: '',
  logo_base64: '',
};

const ProfilEntreprisePage = () => {
  const { user, refreshUser } = useAuth();
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user) {
      setForm({
        raison_sociale: user.raison_sociale || '',
        adresse: user.adresse || '',
        code_postal: user.code_postal || '',
        ville: user.ville || '',
        siren: user.siren || '',
        siret: user.siret || '',
        code_ape: user.code_ape || '',
        regime_tva: user.regime_tva || 'franchise',
        tva_intracom: user.tva_intracom || '',
        rc_pro_assureur: user.rc_pro_assureur || '',
        rc_pro_contrat: user.rc_pro_contrat || '',
        iban: user.iban || '',
        bic: user.bic || '',
        logo_base64: user.logo_base64 || '',
      });
    }
  }, [user]);

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      toast.error('Format accepté : PNG ou JPEG');
      return;
    }
    const img = new Image();
    img.onload = () => {
      const maxW = 600;
      const scale = Math.min(1, maxW / img.width);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
      if (dataUrl.length > 700000) {
        toast.error('Image trop lourde même après compression, choisissez un logo plus simple');
        return;
      }
      handleChange('logo_base64', dataUrl);
      toast.success('Logo prêt — pensez à enregistrer');
    };
    img.onerror = () => toast.error("Impossible de lire l'image");
    img.src = URL.createObjectURL(file);
    e.target.value = '';
  };

  const isComplete = form.raison_sociale && form.adresse && form.code_postal && form.ville && form.siren;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.siren && !/^\d{9}$/.test(form.siren)) {
      toast.error('Le SIREN doit contenir exactement 9 chiffres');
      return;
    }
    if (form.siret && !/^\d{14}$/.test(form.siret)) {
      toast.error('Le SIRET doit contenir exactement 14 chiffres');
      return;
    }
    setSaving(true);
    try {
      await userAPI.updateEntreprise(form);
      toast.success('Profil entreprise enregistré');
      if (refreshUser) await refreshUser();
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="profil-entreprise-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 bg-[#10B981]/10 rounded-xl">
          <Building2 className="h-7 w-7 text-[#10B981]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mon entreprise</h1>
          <p className="text-sm text-gray-500">
            Ces informations apparaîtront automatiquement sur toutes vos factures.
          </p>
        </div>
      </div>

      {/* Alert conformité */}
      {!isComplete ? (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-amber-900">Profil incomplet</p>
              <p className="text-amber-800">
                Pour émettre des factures conformes à la législation (Art. 289 CGI), renseignez au minimum :
                <strong> raison sociale, adresse, code postal, ville, SIREN</strong>.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-emerald-300 bg-emerald-50">
          <CardContent className="p-4 flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-emerald-900">Profil conforme</p>
              <p className="text-emerald-800">Vos factures respectent les mentions légales obligatoires.</p>
            </div>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Logo entreprise */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <ImagePlus className="h-4 w-4 text-[#10B981]" />
              Logo <span className="text-xs text-muted-foreground font-normal">(optionnel — apparaît en haut de vos factures)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              {form.logo_base64 ? (
                <img
                  src={form.logo_base64}
                  alt="Logo entreprise"
                  className="h-16 max-w-[180px] object-contain border rounded-md bg-white p-1"
                  data-testid="logo-preview"
                />
              ) : (
                <div className="h-16 w-32 border-2 border-dashed rounded-md flex items-center justify-center text-xs text-muted-foreground">
                  Aucun logo
                </div>
              )}
              <div className="flex flex-col gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    className="hidden"
                    onChange={handleLogoUpload}
                    data-testid="input-logo-upload"
                  />
                  <span className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5">
                    <ImagePlus className="h-4 w-4 mr-2" />
                    {form.logo_base64 ? 'Changer le logo' : 'Ajouter un logo'}
                  </span>
                </label>
                {form.logo_base64 && (
                  <button
                    type="button"
                    onClick={() => handleChange('logo_base64', '')}
                    className="inline-flex items-center text-xs text-red-500 hover:text-red-700"
                    data-testid="btn-remove-logo"
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> Supprimer le logo
                  </button>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              PNG ou JPEG. Le logo est automatiquement redimensionné et intégré dans vos factures Factur-X (PDF/A-3).
            </p>
          </CardContent>
        </Card>

        {/* Identité de l'entreprise */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Building2 className="h-4 w-4 text-[#10B981]" />
              Identité de l&apos;entreprise
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="raison_sociale">Raison sociale / Nom commercial *</Label>
              <Input
                id="raison_sociale"
                value={form.raison_sociale}
                onChange={(e) => handleChange('raison_sociale', e.target.value)}
                placeholder="Ex : Cabinet Dupont Conseil"
                data-testid="input-raison-sociale"
              />
              <p className="text-xs text-muted-foreground">
                Si vous exercez en nom propre, indiquez votre nom + prénom.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2 md:col-span-3">
                <Label htmlFor="adresse">Adresse *</Label>
                <Input
                  id="adresse"
                  value={form.adresse}
                  onChange={(e) => handleChange('adresse', e.target.value)}
                  placeholder="12 rue de la République"
                  data-testid="input-adresse"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code_postal">Code postal *</Label>
                <Input
                  id="code_postal"
                  value={form.code_postal}
                  onChange={(e) => handleChange('code_postal', e.target.value.replace(/\D/g, ''))}
                  placeholder="75001"
                  maxLength={5}
                  data-testid="input-code-postal"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="ville">Ville *</Label>
                <Input
                  id="ville"
                  value={form.ville}
                  onChange={(e) => handleChange('ville', e.target.value)}
                  placeholder="Paris"
                  data-testid="input-ville"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Identifiants légaux */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <FileCheck className="h-4 w-4 text-[#10B981]" />
              Identifiants légaux
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="siren">SIREN * <span className="text-xs text-muted-foreground">(9 chiffres)</span></Label>
                <Input
                  id="siren"
                  value={form.siren}
                  onChange={(e) => handleChange('siren', e.target.value.replace(/\D/g, ''))}
                  placeholder="123456789"
                  maxLength={9}
                  data-testid="input-siren"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="siret">SIRET <span className="text-xs text-muted-foreground">(14 chiffres — optionnel)</span></Label>
                <Input
                  id="siret"
                  value={form.siret}
                  onChange={(e) => handleChange('siret', e.target.value.replace(/\D/g, ''))}
                  placeholder="12345678900012"
                  maxLength={14}
                  data-testid="input-siret"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code_ape">Code APE / NAF</Label>
                <Input
                  id="code_ape"
                  value={form.code_ape}
                  onChange={(e) => handleChange('code_ape', e.target.value.toUpperCase())}
                  placeholder="6202A"
                  maxLength={5}
                  data-testid="input-code-ape"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tva_intracom">N° TVA intracommunautaire</Label>
                <Input
                  id="tva_intracom"
                  value={form.tva_intracom}
                  onChange={(e) => handleChange('tva_intracom', e.target.value.toUpperCase().replace(/\s/g, ''))}
                  placeholder="FR12345678901"
                  data-testid="input-tva-intracom"
                />
                <p className="text-xs text-muted-foreground">Uniquement si vous êtes assujetti TVA.</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Régime TVA — Le point clé pour switcher en cours d'année */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <MapPin className="h-4 w-4 text-[#10B981]" />
              Régime de TVA
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="regime_tva">Régime actuel *</Label>
              <select
                id="regime_tva"
                value={form.regime_tva}
                onChange={(e) => handleChange('regime_tva', e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                data-testid="select-regime-tva"
              >
                <option value="franchise">Franchise en base de TVA (auto-entrepreneur classique)</option>
                <option value="reel_simplifie">Régime réel simplifié (assujetti TVA)</option>
                <option value="reel_normal">Régime réel normal (assujetti TVA)</option>
              </select>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-xs text-blue-900 flex items-start gap-2 mt-2">
                <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  <p><strong>Passage à la TVA en cours d&apos;année ?</strong> Modifiez ce champ le jour du dépassement de seuil.</p>
                  <p className="mt-1">Les <strong>factures déjà émises conservent</strong> leur régime d&apos;origine (mention immuable). Les <strong>nouvelles factures</strong> utiliseront le nouveau régime.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Assurance RC Pro (optionnel) */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Shield className="h-4 w-4 text-[#10B981]" />
              Assurance RC Pro <span className="text-xs text-muted-foreground font-normal">(optionnel — obligatoire pour certains métiers réglementés)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="rc_pro_assureur">Assureur</Label>
                <Input
                  id="rc_pro_assureur"
                  value={form.rc_pro_assureur}
                  onChange={(e) => handleChange('rc_pro_assureur', e.target.value)}
                  placeholder="Ex : MAAF, AXA, Hiscox..."
                  data-testid="input-rcpro-assureur"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="rc_pro_contrat">N° de contrat</Label>
                <Input
                  id="rc_pro_contrat"
                  value={form.rc_pro_contrat}
                  onChange={(e) => handleChange('rc_pro_contrat', e.target.value)}
                  placeholder="Ex : CTR-123456"
                  data-testid="input-rcpro-contrat"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Coordonnées bancaires */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Banknote className="h-4 w-4 text-[#10B981]" />
              Coordonnées bancaires <span className="text-xs text-muted-foreground font-normal">(optionnel — apparaît sur les factures)</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="iban">IBAN</Label>
                <Input
                  id="iban"
                  value={form.iban}
                  onChange={(e) => handleChange('iban', e.target.value.toUpperCase())}
                  placeholder="FR76 1234 5678 9012 3456 7890 123"
                  data-testid="input-iban"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bic">BIC</Label>
                <Input
                  id="bic"
                  value={form.bic}
                  onChange={(e) => handleChange('bic', e.target.value.toUpperCase().replace(/\s/g, ''))}
                  placeholder="BNPAFRPPXXX"
                  data-testid="input-bic"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bouton enregistrer sticky */}
        <div className="sticky bottom-4 z-10">
          <Button
            type="submit"
            size="lg"
            disabled={saving}
            className="w-full bg-[#10B981] hover:bg-[#0FA574] shadow-lg"
            data-testid="btn-save-profil-entreprise"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer mes informations'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default ProfilEntreprisePage;
